import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  Loyalty as LoyaltyIcon,
  History as HistoryIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
} from '@mui/icons-material';

function CustomerDashboard() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCustomerData();
  }, []);

  const loadCustomerData = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Customer dashboard loaded data:', result.customer);
          setCustomer(result.customer);
        } else {
          setError(result.message || 'Failed to load customer data');
        }
      } else {
        setError('Failed to load customer data');
      }
    } catch (error) {
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress sx={{ width: '200px' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Alert severity="warning">Customer data not found</Alert>
      </Box>
    );
  }

  const quickActions = [
    {
      title: 'Find Stores',
      description: 'Discover nearby stores',
      icon: <StoreIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      path: '/stores',
      color: '#6366f1'
    },
    {
      title: 'Shop Products',
      description: 'Browse available products',
      icon: <ShoppingCartIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      path: '/products',
      color: '#10b981'
    },
    {
      title: 'View Cart',
      description: 'Check your shopping cart',
      icon: <ShoppingCartIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      path: '/cart',
      color: '#f59e0b'
    },
    {
      title: 'Order History',
      description: 'View past orders',
      icon: <HistoryIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      path: '/orders',
      color: '#3b82f6'
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          Welcome back, {customer.name}!
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Discover great products and manage your shopping experience
        </Typography>
      </Box>

      {/* Customer Info Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 700
              }}
            >
              {customer.name ? customer.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'CU'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {customer.name}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                {customer.email}
              </Typography>
              {customer.mobile && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {customer.mobile}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={`${customer.points_balance || 0} Points`}
                color="primary"
                variant="filled"
                sx={{ fontSize: '1rem', py: 1, px: 2 }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Tier: {customer.points_tier || 'Bronze'}
              </Typography>
            </Box>
          </Box>

          {/* Loyalty Progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Loyalty Progress
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {customer.points_balance || 0} / 500 points to next tier
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(((customer.points_balance || 0) / 500) * 100, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: 'primary.main'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Quick Actions Grid */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Quick Actions
      </Typography>

      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 3,
                boxShadow: 2,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => window.location.href = action.path}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {action.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity Placeholder */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Recent Activity
        </Typography>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              No recent activity to show. Start shopping to see your activity here!
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default CustomerDashboard;